import type React from "react";
import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import { deleteProfilePictureApi, uploadProfilePictureFile } from "../../api/usersApi";
import UserAvatar from "../common/UserAvatar";
import ProfilePictureCropDialog from "./ProfilePictureCropDialog";
import type { ProfileSectionProps } from "./profileSectionTypes";

type TranslationKeys = keyof typeof translations.en;

const ProfilePictureSection: React.FC<ProfileSectionProps> = ({
  user,
  canEdit,
  isSelf,
  onUserUpdated,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (cropImageSrc) {
        URL.revokeObjectURL(cropImageSrc);
      }
    };
  }, [cropImageSrc]);

  const mapUploadError = (err: unknown): string => {
    const message = err instanceof Error ? err.message : "";
    if (message === "PROFILE_PICTURE_TOO_LARGE") {
      return t("profile.picture.tooLarge" as TranslationKeys);
    }
    if (message === "PROFILE_PICTURE_INVALID_TYPE") {
      return t("profile.picture.invalidType" as TranslationKeys);
    }
    if (message === "PROFILE_PICTURE_UPLOAD_FAILED") {
      return t("profile.picture.uploadFailed" as TranslationKeys);
    }
    if (message === "PROFILE_PICTURE_CROP_FAILED") {
      return t("profile.picture.cropFailed" as TranslationKeys);
    }
    return t("profile.picture.uploadFailed" as TranslationKeys);
  };

  const closeCropDialog = () => {
    if (cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
    }
    setCropImageSrc(null);
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    setError(null);
    setSuccess(false);

    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError(t("profile.picture.invalidType" as TranslationKeys));
      return;
    }

    if (cropImageSrc) {
      URL.revokeObjectURL(cropImageSrc);
    }
    setCropImageSrc(URL.createObjectURL(file));
  };

  const handleCropConfirm = async (file: File) => {
    closeCropDialog();
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const updated = await uploadProfilePictureFile(file, isSelf ? undefined : user._id);
      onUserUpdated(updated);
      setSuccess(true);
    } catch (err) {
      setError(mapUploadError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const updated = await deleteProfilePictureApi(isSelf ? undefined : user._id);
      onUserUpdated(updated);
      setSuccess(true);
    } catch {
      setError(t("profile.picture.removeFailed" as TranslationKeys));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("profile.picture.title" as TranslationKeys)}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <UserAvatar user={user} size={72} />
        {canEdit ? (
          <Stack spacing={1}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              hidden
              onChange={handleFileSelected}
            />
            <Button
              variant="outlined"
              size="small"
              disabled={loading}
              onClick={() => fileInputRef.current?.click()}
            >
              {loading ? (
                <CircularProgress size={18} />
              ) : (
                t("profile.picture.upload" as TranslationKeys)
              )}
            </Button>
            {user.profilePictureUrl ? (
              <Button
                variant="text"
                color="error"
                size="small"
                disabled={loading}
                onClick={handleRemove}
              >
                {t("profile.picture.remove" as TranslationKeys)}
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
      {canEdit ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {t("profile.picture.hint" as TranslationKeys)}
        </Typography>
      ) : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 1 }}>
          {t("Success" as TranslationKeys)}
        </Alert>
      ) : null}

      {cropImageSrc ? (
        <ProfilePictureCropDialog
          open
          imageSrc={cropImageSrc}
          onCancel={closeCropDialog}
          onConfirm={handleCropConfirm}
        />
      ) : null}
    </Box>
  );
};

export default ProfilePictureSection;
